import {
  HullConfigurationType,
  HullOptionType,
  SpecializedHullType,
  AdditionalHullType,
  ArmorType,
  StreamlinedType,
} from "../types/hull";

// Base constants
export const HULL = {
  BASE_COST: 50000, // Cr per ton
  MIN_SIZE: 5, // tons
  MIN_JUMP_SIZE: 100, // tons
  POINTS_PER_TON: 0.4, // 1 point per 2.5 tons
  MASSIVE_POINTS: {
    MEDIUM: 0.5, // 1 point per 2 tons (25,000-99,999 tons)
    LARGE: 0.67, // 1 point per 1.5 tons (100,000+ tons)
  },
} as const;

// Hull configurations
export const HULL_CONFIGURATIONS = [
  {
    type: "standard" as HullConfigurationType,
    name: "Standard",
    description:
      "Boxy, cylindrical, series of joined boxes, possibly with rounded edges",
    examples: "yacht, subsidised liner",
    streamlined: "partial" as StreamlinedType,
    armorVolumeModifier: 1,
    hullPointsModifier: 1,
    costModifier: 1,
    costPerTon: 50000,
    weightModifier: 1,
    hullPointsPerTon: 2.5,
  },
  {
    type: "streamlined" as HullConfigurationType,
    name: "Streamlined",
    description:
      "Needle, wedge, disc, lifting body, lozenge, boxy with rounded nose",
    examples: "pinnace, shuttle, scout/courier, free trader",
    streamlined: "yes" as StreamlinedType,
    armorVolumeModifier: 1.2,
    hullPointsModifier: 1,
    costModifier: 1.2,
    costPerTon: 50000,
    weightModifier: 1,
    hullPointsPerTon: 2.5,
  },
  {
    type: "sphere" as HullConfigurationType,
    name: "Sphere",
    description: "Sphere or large spheroid with limited protuberances",
    examples: "express boat, mercenary cruiser",
    streamlined: "partial" as StreamlinedType,
    armorVolumeModifier: 0.9,
    hullPointsModifier: 1,
    costModifier: 1.1,
    costPerTon: 50000,
    weightModifier: 1,
    hullPointsPerTon: 2.5,
  },
  {
    type: "closeStructure" as HullConfigurationType,
    name: "Close Structure",
    description:
      "Boxes joined by sturdy struts or with multiple projecting elements",
    examples:
      "Chrysanthemum-class destroyer escort, Gazelle-class close escort",
    streamlined: "partial" as StreamlinedType,
    armorVolumeModifier: 1.5,
    hullPointsModifier: 1,
    costModifier: 0.8,
    costPerTon: 50000,
    weightModifier: 1,
    hullPointsPerTon: 2.5,
  },
  {
    type: "dispersedStructure" as HullConfigurationType,
    name: "Dispersed Structure",
    description: "Irregular joined shapes, open framework, rings",
    examples:
      "Donosev-class survey scout, Lab Ship, Arakoine-class strike cruiser",
    streamlined: "no" as StreamlinedType,
    armorVolumeModifier: 2,
    hullPointsModifier: 0.9,
    costModifier: 0.5,
    costPerTon: 50000,
    weightModifier: 1,
    hullPointsPerTon: 2.5,
  },
  {
    type: "planetoid" as HullConfigurationType,
    name: "Planetoid",
    description: "Generally lumpy, rounded potato shapes",
    examples: "Planetoid Monitor",
    streamlined: "no" as StreamlinedType,
    armorVolumeModifier: 1,
    hullPointsModifier: 1.25,
    costModifier: 1.5,
    costPerTon: 4000,
    weightModifier: 1,
    hullPointsPerTon: 2.5,
    usableVolume: 0.8,
    baseCost: 4000,
  },
  {
    type: "bufferedPlanetoid" as HullConfigurationType,
    name: "Buffered Planetoid",
    description: "Enhanced planetoid hull with additional protection",
    examples: "Planetoid Monitor",
    streamlined: "no" as StreamlinedType,
    armorVolumeModifier: 1,
    hullPointsModifier: 1.5,
    costModifier: 2.0,
    costPerTon: 4000,
    weightModifier: 1,
    hullPointsPerTon: 2.5,
    usableVolume: 0.65,
    baseCost: 4000,
  },
] as const;

// Hull options
export const HULL_OPTIONS = {
  heatShielding: {
    type: "heatShielding" as HullOptionType,
    name: "Heat Shielding",
    description: "Protects against re-entry heat and star proximity",
    techLevel: 6,
    costPerTon: 100000, // MCr0.1 per ton
    requirements: [],
  },
  radiationShielding: {
    type: "radiationShielding" as HullOptionType,
    name: "Radiation Shielding",
    description: "Improves crew protection against radiation",
    techLevel: 7,
    costPerTon: 25000,
    requirements: [],
  },
  reflec: {
    type: "reflec" as HullOptionType,
    name: "Reflec",
    description: "Increases Protection against lasers by +3",
    techLevel: 10,
    costPerTon: 100000, // MCr0.1 per ton
    requirements: ["!stealth"],
  },
  solarCoating: {
    type: "solarCoating" as HullOptionType,
    name: "Solar Coating",
    description: "Provides basic power needs and backup power",
    techLevel: 7,
    costPerTon: 0, // Deprecated, use costPerUnit below
    requirements: ["!stealth", "!reflec"],
    costPerUnit: {
      6: 0,
      8: 0,
      10: 300000, // MCr0.3 per unit (percent)
      12: 400000, // MCr0.4 per unit (percent)
    },
    powerPerUnit: {
      6: 0,
      8: 0,
      10: 0.1, // Power per unit (percent)
      12: 0.2, // Power per unit (percent)
    },
  },
  stealth: {
    type: "stealth" as HullOptionType,
    name: "Stealth",
    description: "Absorbs radar and lidar beams, disguises heat emissions",
    techLevel: 8,
    costPerTon: 40000, // MCr0.04 per ton
    requirements: ["!reflec"],
    types: [
      {
        type: "basic",
        techLevel: 8,
        costPerTon: 40000, // MCr0.04 per ton
        dm: -2,
        tonnage: 0.02, // 2% of hull
      },
      {
        type: "improved",
        techLevel: 10,
        costPerTon: 100000, // MCr0.1 per ton
        dm: -2,
        tonnage: 0,
      },
      {
        type: "enhanced",
        techLevel: 12,
        costPerTon: 500000, // MCr0.5 per ton
        dm: -4,
        tonnage: 0,
      },
      {
        type: "advanced",
        techLevel: 14,
        costPerTon: 1000000, // MCr1.0 per ton
        dm: -6,
        tonnage: 0,
      },
    ],
  },
} as const;

// Specialized hull types
export const SPECIALIZED_HULL_TYPES = {
  reinforced: {
    type: "reinforced" as SpecializedHullType,
    name: "Reinforced Hull",
    description: "Increases Hull points by +10%",
    costModifier: 1.5,
    hullPointsModifier: 1.1,
    requirements: [],
  },
  light: {
    type: "light" as SpecializedHullType,
    name: "Light Hull",
    description: "Decreases Hull points by -10%",
    costModifier: 0.75,
    hullPointsModifier: 0.9,
    requirements: [],
  },
  military: {
    type: "military" as SpecializedHullType,
    name: "Military Hull",
    description: "Allows double standard armor rating",
    costModifier: 1.25,
    armorMultiplier: 2,
    requirements: ["minSize:5000"],
  },
  nonGravity: {
    type: "nonGravity" as SpecializedHullType,
    name: "Non-Gravity Hull",
    description: "No artificial gravity, uses spinning for gravity",
    costModifier: 0.5,
    powerModifier: 0.5,
    maxSize: 500000,
    requirements: [],
  },
} as const;

// Additional hull types
export const ADDITIONAL_HULL_TYPES = {
  doubleHull: {
    type: "doubleHull" as AdditionalHullType,
    name: "Double Hull",
    description: "Two-hulled cylinder with spinning outer hull for gravity",
    minOuterHullSize: 60,
    spinMachineryPerTon: 0.1,
    costModifier: 0.01, // +1% per percent of spun hull
    requirements: [],
  },
  hamsterCage: {
    type: "hamsterCage" as AdditionalHullType,
    name: "Hamster Cage",
    description: "Series of counter-rotating rings for gravity",
    minRingRadius: 15,
    spinMachineryPerTon: 0.1,
    costModifier: 0.02, // +2% per percent of spun rings
    requirements: [],
  },
  breakaway: {
    type: "breakaway" as AdditionalHullType,
    name: "Breakaway Hulls",
    description: "Can operate as two or more independent vessels",
    tonnageConsumed: 0.02, // 2% of combined hull
    costPerTon: 2000000, // MCr2 per ton
    costModifier: 1, // No additional cost modifier, cost is per ton
    separationTime: "1D rounds",
    requirements: [],
  },
} as const;

// Armor types
export const ARMOR_TYPES = {
  TITANIUM_STEEL: {
    type: "titaniumSteel" as ArmorType,
    name: "Titanium Steel",
    techLevel: 7,
    tonnageConsumed: 0.025, // 2.5%
    costPerTon: 50000,
    maxProtection: (techLevel: number) => Math.min(techLevel, 9),
  },
  CRYSTALIRON: {
    type: "crystaliron" as ArmorType,
    name: "Crystaliron",
    techLevel: 10,
    tonnageConsumed: 0.0125, // 1.25%
    costPerTon: 200000,
    maxProtection: (techLevel: number) => Math.min(techLevel, 13),
  },
  BONDED_SUPERDENSE: {
    type: "bondedSuperdense" as ArmorType,
    name: "Bonded Superdense",
    techLevel: 14,
    tonnageConsumed: 0.008, // 0.80%
    costPerTon: 500000,
    maxProtection: (techLevel: number) => techLevel,
  },
  MOLECULAR_BONDED: {
    type: "molecularBonded" as ArmorType,
    name: "Molecular Bonded",
    techLevel: 16,
    tonnageConsumed: 0.005, // 0.50%
    costPerTon: 1500000, // MCr1.5
    maxProtection: (techLevel: number) => techLevel + 4,
    special: "Tachyon weapons lose AP trait",
  },
} as const;

// Export the base constants with proper names
export const HULL_COSTS = {
  BASE_HULL_COST: HULL.BASE_COST,
} as const;

export const HULL_SIZES = {
  MIN: HULL.MIN_SIZE,
  MIN_JUMP: HULL.MIN_JUMP_SIZE,
  TINY: { min: 1, max: 10, name: "Tiny" },
  SMALL: { min: 11, max: 100, name: "Small" },
  MEDIUM: { min: 101, max: 1000, name: "Medium" },
  LARGE: { min: 1001, max: 10000, name: "Large" },
  HUGE: { min: 10001, max: 100000, name: "Huge" },
} as const;

// Add the missing utility functions
export const calculateHullCost = (
  size: number,
  configuration: (typeof HULL_CONFIGURATIONS)[number],
): number => {
  let cost = size * (configuration.costPerTon || HULL.BASE_COST);
  cost *= configuration.costModifier;
  return cost;
};

export const calculateHullTons = (
  size: number,
  configuration: (typeof HULL_CONFIGURATIONS)[number],
): number => {
  const usableVolume =
    "usableVolume" in configuration ? configuration.usableVolume : 1;
  return size * usableVolume;
};

export const calculateHullPoints = (
  size: number,
  configuration: (typeof HULL_CONFIGURATIONS)[number],
  specializedType?: string | null,
): number => {
  let basePoints = size * (configuration.hullPointsPerTon || 2.5);

  // Apply specialized type modifiers
  if (specializedType) {
    const specialized =
      SPECIALIZED_HULL_TYPES[
        specializedType as keyof typeof SPECIALIZED_HULL_TYPES
      ];
    if (
      specialized &&
      "hullPointsModifier" in specialized &&
      specialized.hullPointsModifier
    ) {
      basePoints *= specialized.hullPointsModifier;
    }
  }

  return Math.floor(basePoints);
};

export const validateHullSpec = (size: number): string[] => {
  const errors: string[] = [];
  if (size < HULL.MIN_SIZE) {
    errors.push(`Hull size must be at least ${HULL.MIN_SIZE} tons`);
  }
  return errors;
};

// Validation functions
export const validateHullConfiguration = (
  config: (typeof HULL_CONFIGURATIONS)[number],
): string[] => {
  const errors: string[] = [];

  if (!config.type) {
    errors.push("Configuration type is required");
  }

  if (!config.name) {
    errors.push("Configuration name is required");
  }

  if (config.armorVolumeModifier <= 0) {
    errors.push("Armor volume modifier must be positive");
  }

  if (config.hullPointsModifier <= 0) {
    errors.push("Hull points modifier must be positive");
  }

  if (typeof config.costModifier === "number" && config.costModifier <= 0) {
    errors.push("Cost modifier must be positive");
  }

  return errors;
};

export const validateHullOption = (
  option: (typeof HULL_OPTIONS)[keyof typeof HULL_OPTIONS],
): string[] => {
  const errors: string[] = [];

  if (!option.type) {
    errors.push("Option type is required");
  }

  if (!option.name) {
    errors.push("Option name is required");
  }

  if (option.techLevel < 0) {
    errors.push("Tech level cannot be negative");
  }

  if (option.costPerTon < 0) {
    errors.push("Cost per ton cannot be negative");
  }

  return errors;
};

export const validateArmorType = (
  armor: (typeof ARMOR_TYPES)[keyof typeof ARMOR_TYPES],
): string[] => {
  const errors: string[] = [];

  if (!armor.type) {
    errors.push("Armor type is required");
  }

  if (!armor.name) {
    errors.push("Armor name is required");
  }

  if (armor.techLevel < 0) {
    errors.push("Tech level cannot be negative");
  }

  if (armor.tonnageConsumed <= 0) {
    errors.push("Tonnage consumed must be positive");
  }

  if (armor.costPerTon <= 0) {
    errors.push("Cost per ton must be positive");
  }

  return errors;
};

export const getHullSpec = (size: number) => {
  const specs = [
    { minSize: 0, maxSize: 1000, multiplier: 1 },
    { minSize: 1001, maxSize: 10000, multiplier: HULL.MASSIVE_POINTS.MEDIUM },
    {
      minSize: 10001,
      maxSize: Infinity,
      multiplier: HULL.MASSIVE_POINTS.LARGE,
    },
  ];
  return specs;
};

export { SPECIALIZED_HULL_TYPES as SPECIALIZED_HULLS };
export { ADDITIONAL_HULL_TYPES as ADDITIONAL_HULLS };
